import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { CreateJobRequest } from '~/common/entities/Project/CreateJobRequest';
import { EditJobRequest } from '~/common/entities/Project/EditJobRequest';
import { ID, IdProvider, unwrap } from '../common/apiCommons';
import { Job } from '~/common/entities/Project/ProjectTypes';
import axios from 'axios';
import _ from 'lodash';

export interface EditJobResponse {
  job: Job;
  possibleNewDepartmentId: number;
}

export enum JobKeys {
  JOBS = 'JOBS',
  JOB = 'JOB',
}
export type JobDuplicateRequest = {
  new_project_id: ID;
  job_id: ID;
};
export const JobQueries = {
  async getAllJobsOfProject(project: IdProvider) {
    if (!project?.id) {
      return null;
    }
    const res = await axios.get<Job[]>(`/jobs/${project.id}/`);
    return unwrap(res);
  },
  async getJob(job: IdProvider) {
    if (!job?.id) {
      return null;
    }
    const res = await axios.get<Job>(`/job/${job.id}/`);
    return unwrap(res);
  },
};

export const JobMutations = {
  async createJob(request: CreateJobRequest) {
    const res = await axios.post<Job>('/job/', request);
    return unwrap(res);
  },
  async editJob(request: EditJobRequest & IdProvider) {
    if (!request.id) {
      return null;
    }
    const res = await axios.put<EditJobResponse>(
      `/job/${request.id}/`,
      _.omit(request, 'id'),
    );
    return unwrap(res);
  },
  async duplicateJob(r: JobDuplicateRequest) {
    if (!r?.job_id) {
      return;
    }
    const { job_id, ...request } = r;
    const res = await axios.post<Job>(`/job/${job_id}/duplicate/`, request);
    return unwrap(res);
  },
  async deleteJob(job: IdProvider) {
    if (!job?.id) {
      return;
    }
    const res = await axios.delete<void>(`/job/${job.id}/`);
    return unwrap(res);
  },
  async mergeJobs(jobs: IdProvider[]) {
    const first = _.first(jobs)?.id;
    if (!first) {
      return null;
    }
    const res = await axios.patch<Job>(`/job/merge/${first}/`, {
      jobs: _.drop(jobs)
        .map((job) => job?.id)
        .filter((jobId) => !!jobId),
    });
    return unwrap(res);
  },
};

export const JobHooks = {
  useJobsOfProjectQuery(
    project: IdProvider,
    options?: UseQueryOptions<Job[] | null>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobKeys.JOBS, project?.id],
      queryFn: () => JobQueries.getAllJobsOfProject(project),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([JobKeys.JOBS, project?.id]),
    ];
    return QueryResultAndInvalidate;
  },
  useGetJobQuery(job: IdProvider, options?: UseQueryOptions<Job | null>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [JobKeys.JOB, job?.id],
      queryFn: () => JobQueries.getJob(job),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([JobKeys.JOB, job?.id]),
    ];
    return QueryResultAndInvalidate;
  },
  useCreateJobMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: JobMutations.createJob,
      async onSuccess(_, project) {
        await queryClient.invalidateQueries([JobKeys.JOBS, project.project]);
      },
    });
  },
  useEditJobMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: JobMutations.editJob,
      async onSuccess(editJobResponse) {
        await queryClient.invalidateQueries([
          JobKeys.JOB,
          editJobResponse?.job?.id,
        ]);
        await queryClient.invalidateQueries([
          JobKeys.JOBS,
          _.first(editJobResponse?.job?.projects)?.id,
        ]);
      },
    });
  },
  useDuplicateJobToProjectMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (r: { new_project_id: ID; job_id: ID }) =>
        JobMutations.duplicateJob(r),

      async onSuccess() {
        await queryClient.invalidateQueries([JobKeys.JOBS]);
      },
    });
  },
  useDeleteJobMutation(project: IdProvider) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: JobMutations.deleteJob,
      async onSuccess() {
        await queryClient.invalidateQueries([JobKeys.JOBS, project?.id]);
      },
    });
  },
  useMergeJobsMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: JobMutations.mergeJobs,
      async onSuccess(data) {
        await queryClient.invalidateQueries([
          JobKeys.JOBS,
          _.first(data?.projects)?.id,
        ]);
      },
    });
  },
};

export const JobApi = {
  queries: JobQueries,
  mutations: JobMutations,
  hooks: JobHooks,
};
